import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageSEO from "@/components/feature/PageSEO";

const siteUrl = (import.meta.env.VITE_SITE_URL ?? "").replace(/\/$/, "");

export default function TermsPage() {
  return (
    <main>
      <PageSEO
        title="Terms of Service | PixelCraft Agency"
        description="Read the Terms of Service for PixelCraft Agency. Understand your rights and obligations when using our affordable small business website design services."
        canonical="/terms"
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-emerald-950 text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-3">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Terms of Service</h1>
          <p className="text-emerald-200/70 text-sm">Last updated: April 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="prose prose-zinc max-w-none text-zinc-700 text-sm leading-relaxed space-y-10">

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">1. Agreement to Terms</h2>
              <p>
                By engaging PixelCraft Agency ("we", "us", "our") for website design and related services, you ("Client") agree to be bound by these Terms of Service. Please read them carefully before placing an order. If you do not agree with any part of these terms, you must not use our services.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">2. Our Services</h2>
              <p>
                PixelCraft Agency provides affordable small business website design packages. Our standard package includes:
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>A professionally designed website of up to five (5) pages</li>
                <li>Domain name registration for one (1) year</li>
                <li>Website hosting for one (1) year</li>
                <li>Mobile-responsive design</li>
                <li>Basic on-page SEO setup</li>
              </ul>
              <p className="mt-3">
                All of the above is provided for an all-inclusive annual fee of <strong>£149.00</strong>. Renewal pricing will be communicated to you no less than 30 days before your renewal date.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">3. Payment Terms</h2>
              <p>
                Payment of £149.00 is due in full before work commences. We accept payment via bank transfer, debit/credit card, or other methods stated on our invoice. All prices are inclusive of VAT where applicable. Failure to pay may result in suspension or termination of your website and associated services.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">4. Client Responsibilities</h2>
              <p>You agree to:</p>
              <ul className="list-disc pl-5 mt-3 space-y-1">
                <li>Provide all required content (text, images, logos) in a timely manner</li>
                <li>Ensure all content supplied is accurate, lawful, and does not infringe third-party rights</li>
                <li>Respond to requests for feedback or approval within 5 working days</li>
                <li>Not use the website for any unlawful or harmful purpose</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">5. Intellectual Property</h2>
              <p>
                Upon receipt of full payment, you are granted a non-exclusive licence to use the completed website design. PixelCraft Agency retains the right to display the completed work in our portfolio unless you request otherwise in writing. Any third-party assets (fonts, stock images, plugins) remain subject to their respective licences.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">6. Revisions & Scope</h2>
              <p>
                Our standard package includes up to two (2) rounds of revisions. Additional revisions or pages beyond the agreed scope may be subject to additional charges, which will be agreed in writing before work proceeds.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">7. Domain Name Registration</h2>
              <p>
                We will register a domain name on your behalf as part of the package. The domain will be registered in your name and you retain full ownership. We will transfer management access to you upon request. Domain availability cannot be guaranteed; if your preferred domain is unavailable, we will work with you to find a suitable alternative.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">8. Renewals & Cancellation</h2>
              <p>
                Your package renews annually. We will contact you at least 30 days before renewal. If you choose not to renew, your website and domain will remain active until the end of the current paid period, after which they may be suspended. We do not offer refunds for partial periods.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">9. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, PixelCraft Agency shall not be liable for any indirect, incidental, or consequential loss arising from the use of, or inability to use, your website. Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">10. Governing Law</h2>
              <p>
                These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">11. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time. We will notify you of any material changes by email or by posting a notice on our website. Continued use of our services after changes take effect constitutes your acceptance of the revised Terms.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-zinc-900 mb-3">12. Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us at{" "}
                <a
                  href="mailto:hello@pixelcraft.agency"
                  className="text-emerald-700 hover:text-emerald-900 underline"
                >
                  hello@pixelcraft.agency
                </a>
                .
              </p>
            </div>

          </div>

          {/* Back link */}
          <div className="mt-14 pt-8 border-t border-zinc-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a
              href="/privacy"
              className="text-sm text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
            >
              Read our Privacy Policy
            </a>
            <a
              href="/contact"
              className="ml-auto whitespace-nowrap bg-accent hover:bg-accent-dark text-white text-sm font-bold px-5 py-2.5 rounded-lg transition-all cursor-pointer"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
